import { useTranslation } from "react-i18next";
import {
  type KeywordFeedbackDetails,
  KeywordFeedbackSubCategory,
} from "reedy-data/api";
import { Button } from "components/button";
import { Text } from "components/text";
import {
  getCategoryLabel,
  getSubCategoryLabel,
} from "./KeywordFeedbackPopover.labels";

interface KeywordFeedbackSummaryProps {
  keyword: string;
  details: KeywordFeedbackDetails;
  onEdit: () => void;
  onRemove: () => Promise<void>;
}

export const KeywordFeedbackSummary = ({
  keyword,
  details,
  onEdit,
  onRemove,
}: KeywordFeedbackSummaryProps) => {
  const { t } = useTranslation(["components"]);

  return (
    <div className="px-4 py-3 flex flex-col gap-3">
      <div
        className="flex flex-col gap-1"
        data-testid={`feedback-summary-${keyword}`}
      >
        <Text as="span" variant="labelSmall">
          {getCategoryLabel(t, details.category)}
        </Text>
        {details.subCategory &&
          details.subCategory !== KeywordFeedbackSubCategory.OTHER && (
            <Text as="span" variant="caption">
              ↳ {getSubCategoryLabel(t, details.subCategory)}
            </Text>
          )}
        {details.freeText && (
          <Text as="span" variant="captionItalic" className="break-words">
            {`"${details.freeText}"`}
          </Text>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          variant="tertiary"
          size="small"
          type="button"
          onClick={onRemove}
          className="h-auto px-2.5 py-1.5 text-xs font-medium rounded-lg border-danger-200 bg-white text-danger-600 hover:bg-danger-50 hover:border-danger-300 hover:text-danger-700"
          data-testid={`feedback-remove-${keyword}`}
        >
          {t("components:keywordsTab.keywordFeedbackPopover.remove")}
        </Button>
        <Button
          size="small"
          type="button"
          onClick={onEdit}
          className="h-auto px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
          data-testid={`feedback-edit-${keyword}`}
        >
          {t("components:keywordsTab.keywordFeedbackPopover.edit")}
        </Button>
      </div>
    </div>
  );
};
